import type { TFunction } from 'i18next';
import type { LayoutItem } from 'react-grid-layout';
import type {
  Component,
  ComponentData,
  DataEntryComponent,
  LayoutData,
} from '../../api/pages-client/pages-client.type';
import type {
  CustomSprintMeasureDataResponse,
  WorkPeriod,
} from '../../api/work-periods-client/work-periods-client.type';
import { WorkPeriodTense } from '../../api/work-periods-client/work-periods-client.type';
import { formatDate } from '../../helpers/date-time-helpers/formatting/formatting';
import { safeParseDateInput } from '../../helpers/date-time-helpers/parsing/parsing';
import { DATE_RANGES, getSprintDateRanges } from './dashboards.type';

/**
 * Returns the appropriate icon name for a given widget type and subtype
 *
 * @param type - The main widget type ('chart', 'table', 'tile', 'metric')
 * @param subType - Optional subtype for chart widgets ('line', 'area', 'bar', 'stacked-bar', 'pie')
 * @returns The icon name string to be used with the Icon component
 */
const getWidgetIcon = (type: string, subType?: string): string => {
  switch (type) {
    case 'data_entry':
      return 'data_object';
    case 'chart':
      switch (subType) {
        case 'line':
          return 'show_chart';
        case 'area':
          return 'area_chart';
        case 'bar':
          return 'bar_chart';
        case 'stacked-bar':
          return 'stacked_bar_chart';
        case 'pie':
          return 'pie_chart';
        case 'burn_up':
          return 'trending_up';
        case 'burn_down':
          return 'trending_down';
        case 'cumulative_flow':
          return 'waterfall_chart';
        default:
          return 'analytics';
      }
    case 'table':
      return 'table_chart';
    case 'tile':
      return 'numbers';
    case 'metric':
      return 'speed';
    case 'text':
      return 'text_fields';
    default:
      return 'dashboard';
  }
};

/**
 * Calculates the date range for a dashboard component based on its range type.
 *
 * For `range_type === 'custom'`, returns the explicit `start_date`/`end_date`
 * stored on the component. Falls back to the default 30-day window if either is
 * missing — this is defensive and should not normally occur because the modal
 * blocks Step 1 until both dates are entered.
 *
 * @param {ComponentData} componentData - The component data containing the range_type (and start_date/end_date when custom)
 * @returns {{ startDate: string, endDate: string }} Object containing ISO date strings for start and end dates
 *
 * @example
 * const component = { range_type: 'last_30_days' };
 * const { startDate, endDate } = getComponentDateRange(component);
 * Returns dates 30 days apart with endDate as today
 */
const getComponentDateRange = (componentData: ComponentData): { startDate: string; endDate: string } => {
  if (componentData.range_type === 'custom' && componentData.start_date && componentData.end_date) {
    return { startDate: componentData.start_date, endDate: componentData.end_date };
  }

  const endDate = new Date().toISOString().split('T')[0];
  let days = 30;
  switch (componentData.range_type) {
    case 'today':
      days = 0;
      break;
    case 'last_1_day':
      days = 1;
      break;
    case 'last_7_days':
      days = 7;
      break;
    case 'last_14_days':
      days = 14;
      break;
    case 'last_30_days':
      days = 30;
      break;
    case 'last_60_days':
      days = 60;
      break;
    case 'last_90_days':
      days = 90;
      break;
    case 'last_sprint':
    case 'last_2_sprints':
    case 'last_3_sprints':
    case 'last_4_sprints':
    case 'last_5_sprints':
    case 'last_6_sprints':
      days = getComponentNSprints(componentData) * 14;
      break;
  }
  const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  if (componentData.range_type === 'last_1_day') {
    return { startDate: startDate, endDate: startDate };
  }
  return { startDate, endDate };
};

/**
 * Calculates the number of sprints for a dashboard component based on its range type
 *
 * @param {ComponentData} componentData - The component data containing the range_type
 * @returns {number} The number of sprints
 *
 * @example
 * const component = { range_type: 'last_sprint' };
 * const nSprints = getComponentNSprints(component);
 * Returns 1
 */
const getComponentNSprints = (componentData: ComponentData): number => {
  switch (componentData.range_type) {
    case 'last_sprint':
      return 1;
    case 'last_2_sprints':
      return 2;
    case 'last_3_sprints':
      return 3;
    case 'last_4_sprints':
      return 4;
    case 'last_5_sprints':
      return 5;
    case 'last_6_sprints':
      return 6;
    default:
      return 1;
  }
};

/**
 * Reports whether a chart date falls on a calendar day after today.
 *
 * Used to suppress data points for not-yet-elapsed days on in-progress sprint
 * charts: future days are kept on the x-axis but rendered as gaps rather than a
 * misleading zero. Today itself is NOT future, so the current day still renders.
 *
 * @param {string} date - The data point's date (ISO date or datetime string).
 * @param {Date} now - Reference "now"; defaults to the current time. Injectable for tests.
 * @returns {boolean} True when `date`'s calendar day is strictly after `now`'s.
 */
const isFutureDate = (date: string, now: Date = new Date()): boolean => {
  const parsed = safeParseDateInput(date);
  if (!parsed) {
    return false;
  }
  return parsed.isAfter(now, 'day');
};

/**
 * Selects which sprint work periods a sprint widget should chart, newest first.
 *
 * Responsibilities:
 * 1. Drop work periods without an end_date and any FUTURE-tense sprint.
 * 2. When the active sprint is excluded, also drop the in-progress (PRESENT) sprint.
 * 3. Sort by end_date descending and keep the most recent `nSprints`.
 *
 * Because the active sprint's end_date is in the future it sorts first, so
 * including it yields the current sprint plus the (nSprints - 1) most recent
 * completed sprints (nSprints total). With no active sprint present, this
 * naturally falls back to the last `nSprints` completed sprints.
 *
 * @param {WorkPeriod[]} workPeriods - Candidate sprint work periods for the board.
 * @param {number} nSprints - Maximum number of sprints to return.
 * @param {boolean} includeCurrentSprint - Whether the active (PRESENT) sprint is eligible.
 * @returns {WorkPeriod[]} Up to `nSprints` work periods ordered by end_date descending.
 */
const selectSprintWorkPeriods = (
  workPeriods: WorkPeriod[],
  nSprints: number,
  includeCurrentSprint: boolean,
): WorkPeriod[] =>
  workPeriods
    .filter((workPeriod) => {
      if (!workPeriod.end_date) return false;
      if (workPeriod.time_tense === WorkPeriodTense.Future) return false;
      if (!includeCurrentSprint && workPeriod.time_tense === WorkPeriodTense.Present) return false;
      return true;
    })
    .sort((a, b) => new Date(b.end_date).getTime() - new Date(a.end_date).getTime())
    .slice(0, nSprints);

/**
 * Checks if an object is an instance of CustomMeasureDataByEntity
 *
 * @param obj - The object to check
 * @returns {boolean} True if the object is an instance of CustomMeasureDataByEntity, false otherwise
 */
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Reserved sibling keys that ride alongside the UUID-keyed entity entries
 * in custom-sprint measure responses. `phases` is the per-workflow display-
 * label companion array forwarded by `attach_phases_payload` on the
 * backend. Skipping these in the type guard keeps phase-aware responses
 * recognized as CustomSprintMeasureDataResponse instead of falling through
 * to the generic per-measure branch.
 */
const NON_ENTITY_RESPONSE_KEYS = new Set(['phases']);

function isCustomSprintMeasureDataResponse(obj: any): obj is CustomSprintMeasureDataResponse {
  if (!obj || typeof obj !== 'object') {
    return false;
  }
  const entityKeys = Object.keys(obj).filter((key) => !NON_ENTITY_RESPONSE_KEYS.has(key));
  return entityKeys.length > 0 && entityKeys.every((key) => UUID_REGEX.test(key));
}
/**
 * Creates a default 3x3 grid layout for widgets without layout data
 *
 * @param {string} componentId - The unique identifier for the component
 * @param {number} index - The index of the component in the array
 * @returns {LayoutItem} A default layout configuration for the component
 */
const createDefaultLayout = (componentId: string, index: number): LayoutItem => {
  const row = Math.floor(index / 3);
  const col = index % 3;

  return {
    i: componentId,
    x: col * 4,
    y: row * 3,
    w: 4,
    h: 3,
  };
};

/**
 * Converts LayoutData object to LayoutItem object required by react-grid-layout
 *
 * @param {LayoutData} layoutData - The layout data stored in the database
 * @param {string} componentId - The unique identifier for the component
 * @returns {LayoutItem} A layout configuration compatible with react-grid-layout
 */
const layoutDataToLayout = (layoutData: LayoutData, componentId: string): LayoutItem => {
  return {
    i: componentId,
    x: layoutData.x,
    y: layoutData.y,
    w: layoutData.w,
    h: layoutData.h,
  };
};

/**
 * Converts LayoutItem object from react-grid-layout to LayoutData for storage
 *
 * @param {LayoutItem} layout - The layout item from react-grid-layout
 * @returns {LayoutData} A simplified layout data object for storage
 */
const layoutToLayoutData = (layout: LayoutItem): LayoutData => {
  return {
    x: layout.x,
    y: layout.y,
    w: layout.w,
    h: layout.h,
  };
};

/**
 * Reconstructs the layout from the components
 *
 * @param {Component[]} components - The components to reconstruct the layout from
 * @param {DataEntryComponent[]} dataEntryComponents - The data entry components to reconstruct the layout from
 * @returns {LayoutItem[]} The reconstructed layout
 */
const reconstructLayoutFromComponents = (
  components: Component[],
  dataEntryComponents: DataEntryComponent[],
): LayoutItem[] => {
  const regularLayouts = (components || [])
    .filter((comp) => comp.id)
    .map((comp, index) => {
      return comp.layout_data !== undefined && Object.keys(comp.layout_data).length > 0
        ? layoutDataToLayout(comp.layout_data, comp.id!)
        : createDefaultLayout(comp.id!, index);
    });

  const dataEntryLayouts = (dataEntryComponents || [])
    .filter((comp) => comp.id)
    .map((comp, index) => {
      const totalRegularWidgets = components?.length || 0;
      const adjustedIndex = totalRegularWidgets + index;
      return comp.layout_data !== undefined && Object.keys(comp.layout_data).length > 0
        ? layoutDataToLayout(comp.layout_data, comp.id!)
        : createDefaultLayout(comp.id!, adjustedIndex);
    });

  return [...regularLayouts, ...dataEntryLayouts];
};

/**
 * Builds a display label for a date range with actual start/end dates from chart data.
 * Example: "Last 90 Days (Jan 18, 2026 - Apr 18, 2026)"
 *
 * @param {string} rangeType - The range_type value (e.g. 'last_90_days')
 * @param {Array<{ date: string }>} chartData - Chart data points containing date strings
 * @param {TFunction} t - Translation function from `useTranslation()` so the
 *   sprint-range labels reflect any org-level text_config override
 * @returns {string | undefined} Formatted label with date range, or just the label if no chart data
 */
const getDateRangeDisplayLabel = (
  rangeType: string | undefined,
  chartData: Array<{ date: string }>,
  t: TFunction,
): string | undefined => {
  const rangeLabel = [...DATE_RANGES, ...getSprintDateRanges(t)].find((range) => range.value === rangeType)?.label;

  if (!rangeLabel || !chartData || chartData.length === 0) {
    return rangeLabel;
  }

  const dates = chartData.map((d) => d.date).sort();
  const startDate = formatDate(dates[0]);
  const endDate = formatDate(dates[dates.length - 1]);
  return `${rangeLabel} (${startDate} - ${endDate})`;
};

export {
  createDefaultLayout,
  getComponentDateRange,
  getComponentNSprints,
  getDateRangeDisplayLabel,
  getWidgetIcon,
  isCustomSprintMeasureDataResponse,
  isFutureDate,
  layoutToLayoutData,
  reconstructLayoutFromComponents,
  selectSprintWorkPeriods,
};
